import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@mui/material';

export interface Column<T> {
  // Broadened to accept React elements (like Dropdowns) or normal strings
  header: React.ReactNode;
  accessor: keyof T | 'actions' | string;
  render?: (row: T) => JSX.Element;
  headerClassName?: string;
  cellClassName?: string;
}

interface LivisGeneralTableProps<T> {
  columns: Column<T>[];
  data: T[];
  tableClassName?: string;
  theadClassName?: string;
  trClassName?: string;
  thClassName?: string;
  tbodyClassName?: string;
  rowClassName?: string;
  tdClassName?: string;
}

const LivisGeneralTable = <T extends { id: number | string }>({
  columns,
  data,
  tableClassName = 'livis-archive-settings-table',
  theadClassName = 'livis-archive-settings-table-head',
  trClassName = 'livis-archive-settings-tr',
  thClassName = 'livis-archive-settings-th',
  tbodyClassName = 'livis-archive-settings-tbody',
  rowClassName = 'livis-archive-settings-row',
  tdClassName = 'livis-archive-settings-td',
}: LivisGeneralTableProps<T>): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  if (isMobile || isTablet) {
    return (
      <div className="livis-archive-settings-mobile-list">
        {data.map((item) => (
          <div key={item.id} className="livis-archive-settings-mobile-card">
            {columns.map((column, colIndex) => (
              <div key={colIndex} className={`livis-archive-settings-mobile-row ${column.accessor === 'actions' ? 'livis-archive-settings-mobile-row-actions' : ''}`}>
                
                {/* SAFETY CHECK: Only call t() if the header is actually a string */}
                {column.accessor !== 'actions' && (
                  <div className="livis-archive-settings-mobile-label">
                    {typeof column.header === 'string' ? t(column.header) : column.header}
                  </div>
                )}
                
                <div className="livis-archive-settings-mobile-value">
                  {column.render ? column.render(item) : column.accessor !== 'actions' ? (item[column.accessor as keyof T] as React.ReactNode) : null}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="livis-archive-settings-table-wrap livis-synthetic-table-wrap livis-archive-settings-table-wrapper">
      <table className={tableClassName}>
        <thead className={theadClassName}>
          <tr className={trClassName}>
            {columns.map((column, index) => (
              <th key={index} className={`${thClassName} ${column.headerClassName || ''}`}>
                {/* Desktop view never used t() in the original code, so this is safe */}
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={tbodyClassName}>
          {data.map((row) => (
            <tr key={row.id} className={rowClassName}>
              {columns.map((column, colIndex) => (
                <td key={colIndex} className={`${tdClassName} ${column.cellClassName || ''}`}>
                  {column.render ? column.render(row) : column.accessor !== 'actions' ? (row[column.accessor as keyof T] as React.ReactNode) : null}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default LivisGeneralTable;